import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import { usePlan } from '../context/PlanContext';
import { cleanOrgName } from '../utils/formatters';
import UpgradeModal from '../components/UpgradeModal';
import OrgChartEditor from '../components/OrgChartEditor';
import AttachOpportunityModal from '../components/AttachOpportunityModal';
import InteractiveOrgChart from '../components/InteractiveOrgChart';
import CreateAgentFromRoleModal from '../components/CreateAgentFromRoleModal';
import OrgChartVisual from '../components/OrgChartVisual';
import CreditCostEstimate from '../components/CreditCostEstimate';
import AgentSuggestionsView from '../components/AgentSuggestionsView';
import UploadsView from '../components/UploadsView';
import OrgChartBottomDrawer from '../components/OrgChartBottomDrawer';
import DuplicateRolesModal from '../components/DuplicateRolesModal';
import OpportunityDetailDrawer from '../components/OpportunityDetailDrawer';

import {
  ArrowRight,
  Buildings,
  CaretRight,
  CircleNotch,
  FileImage,
  FileText,
  Funnel,
  GitBranch,
  Lightning,
  PencilSimple,
  Plus,
  Robot,
  Sparkle,
  Trash,
  TrendUp,
  UploadSimple,
  UsersThree,
  WarningCircle,
  X
} from '@phosphor-icons/react';
const OrgChartPage = () => {
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  const { plan: userPlan } = usePlan();
  const [loading, setLoading] = useState(true);
  const [orgChart, setOrgChart] = useState(null);
  const [tierInfo, setTierInfo] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [sidePanelData, setSidePanelData] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [editorRole, setEditorRole] = useState(null);
  const [isNewRole, setIsNewRole] = useState(false);
  const [showAttachModal, setShowAttachModal] = useState(false);
  // const [exporting, setExporting] = useState(false); // Removed - export buttons removed
  const [uploadedOrgCharts, setUploadedOrgCharts] = useState([]);
  const [parsedOrgCharts, setParsedOrgCharts] = useState([]);
  const [selectedParsedChart, setSelectedParsedChart] = useState(null);
  const [isParsing, setIsParsing] = useState(false);
  const [parsingScanId, setParsingScanId] = useState(null);
  const [showCreateAgentModal, setShowCreateAgentModal] = useState(false);
  const [selectedRoleForAgent, setSelectedRoleForAgent] = useState(null);
  const [activeTab, setActiveTab] = useState('suggestions'); // 'suggestions', 'uploads', or 'visual'
  const [selectedUploadScope, setSelectedUploadScope] = useState('all'); // 'all' or upload_id
  const [selectedNodeId, setSelectedNodeId] = useState(null); // For cross-view linking
  const [opportunities, setOpportunities] = useState([]);
  const [linkedAgents, setLinkedAgents] = useState([]);
  const [showBottomDrawer, setShowBottomDrawer] = useState(false);
  const [selectedRoleData, setSelectedRoleData] = useState(null);
  const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);
  // Opportunity detail drawer state
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [showOpportunityDrawer, setShowOpportunityDrawer] = useState(false);

  // Debug effect to track modal state changes
  useEffect(() => {
    console.log('[MODAL STATE CHANGED]', {
      showCreateAgentModal,
      selectedRoleForAgent,
      activeOrganization: activeOrganization?.organization_id
    });
  }, [showCreateAgentModal, selectedRoleForAgent, activeOrganization]);

  /**
   * CRITICAL UX FIX: Active View Scoping
   * When a specific org chart is selected, filter data to show ONLY that chart's data
   * "All" shows combined view (for users who explicitly want it)
   */
  
  // Get role IDs belonging to the selected upload (for filtering opportunities)
  // Now extracts from the org chart tree using scan_id field
  const selectedUploadRoleIds = useMemo(() => {
    if (selectedUploadScope === 'all') {
      return null; // No filtering - show all
    }
    
    if (!orgChart || !orgChart.root) {
      return new Set();
    }
    
    // Collect all role IDs that have the selected scan_id
    const roleIds = new Set();
    const collectRoleIds = (node) => {
      if (!node) return;
      if (node.scan_id === selectedUploadScope) {
        roleIds.add(node.id);
      }
      if (node.children) {
        node.children.forEach(collectRoleIds);
      }
    };
    collectRoleIds(orgChart.root);
    
    return roleIds;
  }, [selectedUploadScope, orgChart]);

  // Funnel org chart data based on selected upload scope
  // Uses scan_id field on each node for accurate filtering
  const filteredOrgChart = useMemo(() => {
    if (!orgChart || !orgChart.root) return orgChart;
    if (selectedUploadScope === 'all') return orgChart; // Show merged view
    
    // Funnel the tree to only include roles from this upload (matching scan_id)
    const filterTree = (node) => {
      if (!node) return null;
      
      // Virtual root is always included if it has matching children
      if (node.id === 'virtual_root') {
        const filteredChildren = node.children
          ? node.children.map(filterTree).filter(Boolean)
          : [];
        
        if (filteredChildren.length === 0) return null;
        
        return {
          ...node,
          children: filteredChildren
        };
      }
      
      // For regular nodes, check scan_id
      if (node.scan_id !== selectedUploadScope) {
        return null;
      }
      
      const filteredChildren = node.children
        ? node.children.map(filterTree).filter(Boolean)
        : [];
      
      return {
        ...node,
        children: filteredChildren
      };
    };
    
    return {
      ...orgChart,
      root: filterTree(orgChart.root)
    };
  }, [orgChart, selectedUploadScope]);

  // Funnel opportunities based on selected upload scope
  // Uses org_chart_upload_id OR scan_id (how org chart analysis stores them)
  const filteredOpportunities = useMemo(() => {
    if (selectedUploadScope === 'all') {
      return opportunities; // Show all
    }
    
    // Funnel by org_chart_upload_id OR scan_id (both are used in different contexts)
    return opportunities.filter(opp => {
      // Check org_chart_upload_id (from unified assessment)
      if (opp.org_chart_upload_id === selectedUploadScope) {
        return true;
      }
      
      // Check scan_id (from org chart analysis - this is how opportunities are linked!)
      if (opp.scan_id === selectedUploadScope) {
        return true;
      }
      
      // Fallback: Check if linked_role_id belongs to this upload's roles
      if (opp.linked_role_id && selectedUploadRoleIds && selectedUploadRoleIds.size > 0) {
        return selectedUploadRoleIds.has(opp.linked_role_id);
      }
      
      return false;
    });
  }, [opportunities, selectedUploadScope, selectedUploadRoleIds]);

  useEffect(() => {
    if (activeOrganization) {
      fetchTierInfo();
    }
  }, [activeOrganization]);

  useEffect(() => {
    // Check user's plan — Builder and above can access org chart
    const hasAccess = userPlan === 'builder' || userPlan === 'pro' || userPlan === 'partner_pro' || userPlan === 'partner-pro' || userPlan === 'enterprise';
    const isBasicUser = !hasAccess && (!userPlan || userPlan === 'basic' || userPlan === 'starter');
    
    if (tierInfo && !isBasicUser) {
      fetchOrgChart();
      fetchUploadedOrgCharts();
      fetchParsedOrgCharts();
    } else if (tierInfo && isBasicUser) {
      setLoading(false);
    }
  }, [tierInfo, activeOrganization, userPlan]);

  // NOTE: Auto-analysis removed from here - it now happens during upload in assessment

  const fetchTierInfo = async () => {
    if (!activeOrganization) return;
    
    try {
      const url = `${BACKEND_URL}/api/org/settings/tier?organization_id=${activeOrganization.organization_id}`;
      const response = await fetch(url, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setTierInfo(data.tier_info);
      }
    } catch (error) {
      console.error('Error fetching tier info:', error);
    }
  };

  const fetchOrgChart = async () => {
    if (!activeOrganization) return;
    
    console.log('[fetchOrgChart] Starting fetch for org:', activeOrganization.organization_id);
    setLoading(true);
    setError(null);
    try {
      // Use centralized orgApi utility
      const { apiGet } = await import('../utils/orgApi');
      const data = await apiGet('org-chart', activeOrganization.organization_id);
      
      console.log('[fetchOrgChart] Received data:', data);
      console.log('[fetchOrgChart] Setting orgChart to:', data.org_chart);
      setOrgChart(data.org_chart);
    } catch (error) {
      console.error('[fetchOrgChart] Error fetching org chart:', error);
      setError('Error loading org chart');
    } finally {
      setLoading(false);
    }
  };

  const fetchUploadedOrgCharts = async () => {
    if (!activeOrganization) return;
    
    try {
      const url = `${BACKEND_URL}/api/org/org-chart/uploads?organization_id=${activeOrganization.organization_id}`;
      const response = await fetch(url, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setUploadedOrgCharts(data.uploads || []);
      }
    } catch (error) {
      console.error('Error fetching uploaded org charts:', error);
    }
  };

  const fetchParsedOrgCharts = async () => {
    if (!activeOrganization) return;
    
    try {
      const url = `${BACKEND_URL}/api/org/org-chart/parsed?organization_id=${activeOrganization.organization_id}`;
      const response = await fetch(url, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setParsedOrgCharts(data.parsed_charts || []);
      }
    } catch (error) {
      console.error('Error fetching parsed org charts:', error);
    }
  };

  const fetchOpportunities = async () => {
    if (!activeOrganization) return;
    
    try {
      // Only fetch org_chart_analysis opportunities for the Org Chart page
      const response = await fetch(
        `${BACKEND_URL}/api/opportunities?organization_id=${activeOrganization.organization_id}&source=org_chart_analysis`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        // Double-filter to ensure only org chart opportunities are shown
        const orgChartOpps = (data.opportunities || []).filter(
          opp => opp.source === 'org_chart_analysis' || opp.category === 'role'
        );
        setOpportunities(orgChartOpps);
      }
    } catch (error) {
      console.error('Error fetching opportunities:', error);
    }
  };

  useEffect(() => {
    if (activeOrganization) {
      fetchOpportunities();
    }
  }, [activeOrganization]);

  const handleParseOrgChart = async (scanId) => {
    console.log('[handleParseOrgChart] Starting analysis for scanId:', scanId);
    console.log('[handleParseOrgChart] All upload scan_ids:', uploadedOrgCharts.map(u => u.scan_id));
    setIsParsing(true);
    setParsingScanId(scanId);
    console.log('[handleParseOrgChart] Set parsingScanId to:', scanId);
    
    try {
      const url = `${BACKEND_URL}/api/org/org-chart/parse-from-upload`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          upload_id: scanId,
          organization_id: activeOrganization.organization_id
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        console.log('[handleParseOrgChart] Analysis successful, imported_roles_count:', data.imported_roles_count);
        // Refresh both parsed charts AND main org chart
        console.log('[handleParseOrgChart] Calling fetchParsedOrgCharts...');
        await fetchParsedOrgCharts();
        console.log('[handleParseOrgChart] Calling fetchOrgChart...');
        await fetchOrgChart();
        console.log('[handleParseOrgChart] orgChart state after fetch:', orgChart);
        alert(`Successfully analyzed org chart!\n${data.roles_count} roles found in ${data.departments_count} departments.\n${data.imported_roles_count || 0} roles imported.`);
      } else {
        alert(`Failed to analyze org chart: ${data.error}`);
      }
    } catch (error) {
      console.error('Error analyzing org chart:', error);
      alert('Failed to analyze org chart. Please try again.');
    } finally {
      setIsParsing(false);
      setParsingScanId(null);
    }
  };

  const handleDeleteUpload = async (scanId) => {
    if (!window.confirm('Are you sure you want to delete this uploaded org chart? This cannot be undone.')) {
      return;
    }
    
    try {
      const url = `${BACKEND_URL}/api/org/org-chart/upload/${scanId}`;
      const response = await fetch(url, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (response.ok) {
        // Refresh uploaded charts
        await fetchUploadedOrgCharts();
        alert('Upload deleted successfully');
      } else {
        const data = await response.json();
        alert(`Failed to delete upload: ${data.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting upload:', error);
      alert('Failed to delete upload. Please try again.');
    }
  };

  const handleCreateAgentForRole = (roleData) => {
    console.log('[OrgChartPage] handleCreateAgentForRole called with:', roleData);
    console.log('[OrgChartPage] activeOrganization:', activeOrganization);
    setSelectedRoleForAgent(roleData);
    setShowCreateAgentModal(true);
    console.log('[OrgChartPage] Modal state set to true');
  };

  const getNodeStatus = (node) => {
    const overlay = node.ai_overlay || {};
    
    if (overlay.linked_agents_count > 0) {
      return 'ai_active';
    }
    
    // Any opportunity should show as orange (High Priority Opportunity in legend)
    if (overlay.opportunities_count > 0) {
      return 'ai_opportunity';
    }
    
    return 'none';
  };

  const getNodeColor = (status) => {
    switch (status) {
      case 'ai_active':
        return 'from-[#14F195] to-[#10B981]';  // Green - Has AI Agent
      case 'ai_opportunity':
        return 'from-[#F59E0B] to-[#F97316]';  // Orange - High Priority Opportunity
      default:
        return 'from-[#718096] to-[#5C6778]';  // Grey - No AI mapping
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'ai_active':
        return {
          text: 'AI Agent Implemented',
          color: 'bg-[#14F195]/20 text-[#14F195]'
        };
      case 'ai_opportunity':
        return {
          text: 'AI Opportunity',
          color: 'bg-[#F59E0B]/20 text-[#F59E0B]'
        };
      default:
        return {
          text: 'Not Yet Mapped',
          color: 'bg-[#718096]/20 text-[#718096]'
        };
    }
  };

  const handleNodeClick = (node) => {
    setSelectedRole(node);
    setSidePanelData(node);
  };

  const handleCreateAgent = () => {
    if (!selectedRole) return;
    
    // Navigate to Unified Agent Builder 
    navigate('/unified-agent-builder', {
      state: {
        prefillData: {
          name: `${selectedRole.title} AI Assistant`,
          role: selectedRole.title,
          description: `AI assistant for ${selectedRole.title} in ${selectedRole.department} department.${selectedRole.description ? ' ' + selectedRole.description : ''}`
        }
      }
    });
  };

  const handleViewOpportunities = () => {
    if (!selectedRole) return;
    
    // Navigate to opportunities filtered by this role
    navigate(`/opportunities?role=${encodeURIComponent(selectedRole.title)}&department=${encodeURIComponent(selectedRole.department)}`);
  };

  const canEdit = () => {
    // Allow edit for Pro or Partner Pro (supporting both naming conventions)
    return tierInfo && (tierInfo.tier === 'pro' || tierInfo.tier === 'partner_pro' || tierInfo.tier === 'partner-pro');
  };

  const handleAddRole = () => {
    if (!canEdit()) {
      setShowUpgradeModal(true);
      return;
    }
    setEditorRole(null);
    setIsNewRole(true);
    setShowEditor(true);
  };

  const handleEditRole = (role) => {
    if (!canEdit()) {
      setShowUpgradeModal(true);
      return;
    }
    setEditorRole(role);
    setIsNewRole(false);
    setShowEditor(true);
  };

  const handleSaveRole = async (formData) => {
    try {
      const url = isNewRole
        ? `${BACKEND_URL}/api/org/org-chart/role?organization_id=${activeOrganization.organization_id}`
        : `${BACKEND_URL}/api/org/org-chart/role/${editorRole.id}?organization_id=${activeOrganization.organization_id}`;
      
      const response = await fetch(url, {
        method: isNewRole ? 'POST' : 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setShowEditor(false);
        await fetchOrgChart();
      } else {
        alert('Failed to save role. Please try again.');
      }
    } catch (error) {
      console.error('Error saving role:', error);
      alert('An error occurred while saving the role.');
    }
  };

  const handleDeleteRole = async (roleId) => {
    try {
      const url = `${BACKEND_URL}/api/org/org-chart/role/${roleId}?organization_id=${activeOrganization.organization_id}`;
      const response = await fetch(url, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setShowEditor(false);
        await fetchOrgChart();
      } else {
        alert('Failed to delete role. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting role:', error);
      alert('An error occurred while deleting the role.');
    }
  };

  const handleAttachOpportunities = (opportunityIds) => {
    console.log('Attaching opportunities:', opportunityIds);
    setShowAttachModal(false);
  };

  const getExportFileName = (extension) => {
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    let orgSlug = 'organization';
    
    if (activeOrganization?.name) {
      orgSlug = cleanOrgName(activeOrganization.name)
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');
    }
    
    return `org-chart_${orgSlug}_${today}.${extension}`;
  };

  // Export functions removed - Org Chart is exploration view, not exportable deliverable
  // Export functionality will be reintroduced in: Assessment Results, Executive Summary, Reports

  // Handler for view on chart functionality
  const handleViewOnChart = (roleId) => {
    // First, find which upload this role belongs to
    // This ensures the correct org chart is active before navigating
    if (selectedUploadScope !== 'all') {
      // Already viewing a specific chart - just navigate
      setSelectedNodeId(roleId);
      setActiveTab('visual');
    } else {
      // Find which upload contains this role
      for (const parsed of parsedOrgCharts) {
        const roleIds = parsed.parsed_data?.roles?.map(r => r.id) || [];
        if (roleIds.includes(roleId)) {
          // Set the active view to this upload first
          setSelectedUploadScope(parsed.scan_id);
          break;
        }
      }
      setSelectedNodeId(roleId);
      setActiveTab('visual');
    }
    
    // Scroll to top to ensure visual chart is visible
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handler for setting active upload scope
  const handleSetActiveView = (uploadScope) => {
    setSelectedUploadScope(uploadScope);
    // Refresh data with new scope
    fetchOrgChart();
  };

  // Handler for re-analyzing an upload
  const handleReanalyze = async (uploadId) => {
    try {
      // Check if this is first analysis or re-analysis
      const isFirstAnalysis = !parsedOrgCharts.some(p => p.scan_id === uploadId);
      
      const response = await fetch(`${BACKEND_URL}/api/org/org-chart/reanalyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          upload_id: uploadId,
          organization_id: activeOrganization.organization_id
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        // Refresh all data
        fetchOrgChart();
        fetchUploadedOrgCharts();
        fetchParsedOrgCharts();
        fetchOpportunities();
        
        // Show appropriate message based on whether this was first analysis or re-analysis
        const oppsCreated = result.opportunities_created_count || 0;
        const rolesImported = result.imported_roles_count || 0;
        
        if (isFirstAnalysis) {
          alert(`Analysis complete! ${rolesImported} roles imported, ${oppsCreated} AI opportunities identified.`);
        } else {
          alert(`Re-analysis complete! ${rolesImported} roles updated, ${oppsCreated} AI opportunities identified.`);
        }
      } else {
        throw new Error('Failed to analyze');
      }
    } catch (error) {
      console.error('Error analyzing upload:', error);
      alert('Failed to analyze upload');
    }
  };

  // Handler for deleting upload roles
  const handleDeleteUploadRoles = async (uploadId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/org/org-chart/roles`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          upload_id: uploadId,
          organization_id: activeOrganization.organization_id
        })
      });
      
      if (response.ok) {
        // Refresh all data
        fetchOrgChart();
        fetchUploadedOrgCharts();
        fetchParsedOrgCharts();
        alert('Upload roles cleared successfully');
      } else {
        throw new Error('Failed to delete roles');
      }
    } catch (error) {
      console.error('Error deleting upload roles:', error);
      alert('Failed to delete upload roles');
    }
  };

  const renderNode = (node, level = 0) => {
    const status = getNodeStatus(node);
    const badge = getStatusBadge(status);
    const hasChildren = node.children && node.children.length > 0;
    
    return (
      <div key={node.id} className="flex flex-col items-center mb-8" style={{ marginLeft: level > 0 ? '40px' : '0' }}>
        {/* Node */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: level * 0.1 }}
          onClick={() => handleNodeClick(node)}
          className={`relative glass rounded-2xl p-4 cursor-pointer hover:bg-[rgba(255,255,255,0.1)] transition-all min-w-[280px] max-w-[320px] ${
            selectedRole?.id === node.id ? 'ring-2 ring-[#6366F1]' : ''
          }`}
        >
          {/* Status Indicator */}
          <div className={`absolute -top-2 -right-2 w-6 h-6 rounded-full bg-gradient-to-r ${getNodeColor(status)} flex items-center justify-center`}>
            {status === 'ai_active' && <Robot size={14} className="text-white" />}
            {status === 'ai_opportunity' && <Lightning size={14} className="text-white" />}
          </div>
          
          {/* PencilSimple Button (PencilSimple Mode) */}
          {editMode && canEdit() && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleEditRole(node);
              }}
              className="absolute -top-2 -left-2 w-8 h-8 rounded-full bg-[#6366F1] flex items-center justify-center hover:bg-[#5558E3] transition-colors"
            >
              <PencilSimple size={14} className="text-white" />
            </button>
          )}
          
          <div className="flex items-start gap-3">
            <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${getNodeColor(status)} flex items-center justify-center flex-shrink-0`}>
              <UsersThree size={24} className="text-white" />
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-bold truncate">{node.title}</h3>
              {node.name && (
                <p className="text-[#A0AEC0] text-sm truncate">{node.name}</p>
              )}
              <p className="text-[#718096] text-xs mt-1">{node.department}</p>
              
              {/* AI Overlay Info */}
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
                  {badge.text}
                </span>
                {node.ai_overlay && node.ai_overlay.opportunities_count > 0 && (
                  <span className="text-[#A0AEC0] text-xs">
                    {node.ai_overlay.opportunities_count} opp{node.ai_overlay.opportunities_count !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* Children */}
        {hasChildren && (
          <div className="flex flex-col items-start mt-8 pl-8">
            {node.children.map((child, idx) => (
              <div key={child.id} className="relative">
                {idx < node.children.length - 1 && (
                  <div className="absolute left-0 top-0 w-px h-full bg-[rgba(255,255,255,0.1)] ml-4" />
                )}
                {renderNode(child, level + 1)}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Basic tier locked state
  // Check user's plan for gating (Partner Pro users should always have access)
  // Pro tier includes: 'pro', 'partner_pro', 'partner-pro', 'enterprise', etc.
  const isProOrHigher = userPlan === 'pro' || userPlan === 'partner_pro' || userPlan === 'partner-pro' || userPlan === 'enterprise';
  const isBasicUserRender = !isProOrHigher && userPlan !== 'builder' && (userPlan === 'basic' || userPlan === 'starter' || !userPlan);
  
  if (tierInfo && isBasicUserRender) {
    return (
        <>
        <div className="max-w-4xl mx-auto">
            <div className="glass rounded-3xl p-12 text-center">
              <div className="w-20 h-20 rounded-full bg-gradient-to-r from-[#6366F1] to-[#EC4899] flex items-center justify-center mx-auto mb-6">
                <Buildings size={40} className="text-white" />
              </div>
              <h1 className="text-4xl font-bold text-white mb-4">
                Org Chart AI Overlay is a Builder Feature
              </h1>
              <p className="text-xl text-[#A0AEC0] mb-8">
                Upgrade to Builder or higher to visualize where AI fits across your organization.
              </p>
              <div className="flex gap-4 justify-center">
                <button
                  onClick={() => navigate('/pricing')}
                  className="relative overflow-hidden rounded-full group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-[#6366F1] to-[#EC4899]"></div>
                  <span className="relative z-10 flex items-center gap-2 px-8 py-4 text-white font-bold">
                    View Plans
                    <ArrowRight size={20} />
                  </span>
                </button>
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  className="px-8 py-4 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-white font-semibold transition-all"
                >
                  Learn More
                </button>
              </div>
            </div>
          </div>
        {showUpgradeModal && (
          <UpgradeModal
            isOpen={showUpgradeModal}
            onClose={() => setShowUpgradeModal(false)}
            currentTier={tierInfo?.tier || 'basic'}
            message="Unlock Org Chart AI Overlay"
            feature="Org Chart Visualization"
          />
        )}
        </>
    );
  }

  // Loading state
  if (loading) {
    return (
        <div className="flex items-center justify-center py-20">
          <CircleNotch size={48} className="text-[#6366F1] animate-spin" />
        </div>
      
    );
  }

  // Main render - always use a single return statement
  return (
      <>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Buildings size={32} className="text-[#6366F1]" />
              <h1 className="text-4xl font-bold text-white">Organization Chart</h1>
            </div>
            <p className="text-[#A0AEC0]">AI Overlay - Where does AI fit in your organization?</p>
          </div>
          
          {/* PencilSimple Mode hidden for Public Beta - v1 is exploration-only */}
          {orgChart && orgChart.root && canEdit() && false && (
            <div className="flex items-center gap-3">
              <button
                onClick={() => setEditMode(!editMode)}
                className={`px-6 py-3 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-white font-semibold transition-all flex items-center gap-2 ${
                  editMode ? 'ring-2 ring-[#6366F1]' : ''
                }`}
                title="Edit mode coming soon"
              >
                <PencilSimple size={18} />
                {editMode ? 'View Mode' : 'Edit Mode'}
              </button>
              {editMode && (
                <button
                  onClick={handleAddRole}
                  className="px-6 py-3 rounded-full bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white font-semibold flex items-center gap-2 hover:opacity-90 transition-opacity"
                >
                  <Plus size={18} />
                  Add Role
                </button>
              )}
            </div>
          )}
        </div>

        {/* GLOBAL Active View Selector - Visible on ALL tabs */}
        {uploadedOrgCharts.length > 0 && (
          <div className="glass rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <label className="text-white font-semibold flex items-center gap-2">
                <Funnel size={18} className="text-[#6366F1]" />
                Active View:
              </label>
              <select
                value={selectedUploadScope}
                onChange={(e) => handleSetActiveView(e.target.value)}
                className="px-4 py-2 rounded-lg bg-[#1A1B1E] border border-[rgba(255,255,255,0.2)] text-white focus:border-[#6366F1] focus:outline-none min-w-[280px]"
                style={{ colorScheme: 'dark' }}
              >
                <option value="all" className="bg-[#1A1B1E] text-white">Combined View – All Org Charts</option>
                {[...uploadedOrgCharts]
                  .sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at))
                  .map((upload) => {
                    const displayTitle = upload.custom_title || `Org Chart — ${new Date(upload.uploaded_at).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric', 
                      year: 'numeric'
                    })}`;
                    return (
                      <option key={upload.scan_id} value={upload.scan_id} className="bg-[#1A1B1E] text-[#E2E8F0]">
                        {displayTitle}
                      </option>
                    );
                  })}
              </select>
            </div>
            <div className="text-sm text-[#A0AEC0]">
              {selectedUploadScope === 'all' ? (
                <span>Showing all {opportunities.length} AI opportunities</span>
              ) : (
                <span>Showing {filteredOpportunities.length} AI opportunities from this org chart</span>
              )}
            </div>
          </div>
        )}

        {/* Credit cost estimate for analysis operations */}
        <CreditCostEstimate cost={60} label="org chart analysis" className="mt-4" />

        
        {/* Tab Switcher - 3 Tabs for V1 */}
        <div className="flex gap-3">
          <button
            onClick={() => setActiveTab('suggestions')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'suggestions'
                ? 'bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white'
                : 'glass text-[#A0AEC0] hover:text-white hover:bg-[rgba(255,255,255,0.05)]'
            }`}
          >
            <div className="flex items-center gap-2">
              <Sparkle size={18} />
              Agent Suggestions
            </div>
          </button>
          <button
            onClick={() => setActiveTab('uploads')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'uploads'
                ? 'bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white'
                : 'glass text-[#A0AEC0] hover:text-white hover:bg-[rgba(255,255,255,0.05)]'
            }`}
          >
            <div className="flex items-center gap-2">
              <FileImage size={18} />
              Org Charts
            </div>
          </button>
          <button
            onClick={() => setActiveTab('visual')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'visual'
                ? 'bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white'
                : 'glass text-[#A0AEC0] hover:text-white hover:bg-[rgba(255,255,255,0.05)]'
            }`}
          >
            <div className="flex items-center gap-2">
              <GitBranch size={18} />
              Visual Org Chart
            </div>
          </button>
        </div>
        
        {/* Content based on active tab */}
        {activeTab === 'suggestions' ? (
          // Agent Suggestions View (Default) - SCOPED to selected upload
          <AgentSuggestionsView
            parsedOrgCharts={parsedOrgCharts}
            orgChart={filteredOrgChart}
            selectedUploadScope={selectedUploadScope}
            filteredOpportunities={filteredOpportunities}
            onCreateAgent={handleCreateAgentForRole}
            onViewOnChart={handleViewOnChart}
            activeOrganization={activeOrganization}
          />
        ) : activeTab === 'uploads' ? (
          // Uploads View
          <UploadsView
            uploadedOrgCharts={uploadedOrgCharts}
            parsedOrgCharts={parsedOrgCharts}
            selectedUploadScope={selectedUploadScope}
            onSetActiveView={handleSetActiveView}
            onAnalyze={handleReanalyze}
            onDeleteUpload={handleDeleteUploadRoles}
            onRenameUpload={(scanId, newTitle) => {
              // Update local state to reflect the name change
              setUploadedOrgCharts(prev => prev.map(upload => 
                upload.scan_id === scanId 
                  ? { ...upload, custom_title: newTitle }
                  : upload
              ));
            }}
            onUploadComplete={() => {
              // Refresh all data after upload
              fetchUploadedOrgCharts();
              fetchParsedOrgCharts();
              fetchOrgChart();
            }}
            activeOrganization={activeOrganization}
          />
        ) : activeTab === 'visual' ? (
          // Visual Org Chart Tab - SCOPED to selected upload (shows ONE chart at a time)
          filteredOrgChart && filteredOrgChart.root ? (
            <div className="flex flex-col" style={{ height: 'calc(100vh - 200px)' }}>
              {/* Export & Management Buttons */}
              <div className="flex justify-between items-center mb-4">
                <button
                  onClick={() => setShowDuplicatesModal(true)}
                  className="px-4 py-2 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-white transition-all flex items-center gap-2 text-sm font-semibold"
                  title="Find and merge duplicate roles"
                >
                  <GitBranch size={16} />
                  <span className="hidden sm:inline">Smart Merge</span>
                </button>
                {/* Export buttons removed - Org Chart is exploration view, not exportable deliverable */}
              </div>
              
              {/* React Flow Visualization - Shows ONE org chart at a time based on Active View */}
              <div className={`glass rounded-3xl overflow-hidden transition-all ${showBottomDrawer ? 'flex-1' : 'flex-1'}`}>
                <OrgChartVisual
                  orgChartData={filteredOrgChart}
                  selectedNodeId={selectedNodeId}
                  opportunities={filteredOpportunities}
                  linkedAgents={linkedAgents}
                  onNodeClick={(roleId) => {
                    setSelectedNodeId(roleId);
                    // Find role data and show drawer
                    const findRole = (node) => {
                      if (node.id === roleId) return node;
                      if (node.children) {
                        for (const child of node.children) {
                          const found = findRole(child);
                          if (found) return found;
                        }
                      }
                      return null;
                    };
                    const roleData = findRole(orgChart.root);
                    if (roleData) {
                      setSelectedRoleData(roleData);
                      setShowBottomDrawer(true);
                    }
                  }}
                  onNodeDrop={async (draggedRoleId, targetRoleId) => {
                    if (!canEdit()) {
                      setShowUpgradeModal(true);
                      return;
                    }
                    try {
                      const response = await fetch(`${BACKEND_URL}/api/org/org-chart/role/${draggedRoleId}`, {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({
                          organization_id: activeOrganization.organization_id,
                          supervisor_role_id: targetRoleId
                        })
                      });
                      if (response.ok) {
                        fetchOrgChart();
                      }
                    } catch (error) {
                      console.error('Error updating supervisor:', error);
                    }
                  }}
                  onCreateAgent={(roleData) => handleCreateAgentForRole(roleData)}
                  editable={canEdit()}
                />
              </div>
              
              {/* Bottom Drawer - Part of flex layout */}
              {showBottomDrawer && selectedRoleData && (
                <div className="border-t border-[rgba(255,255,255,0.1)]">
                  <OrgChartBottomDrawer
                    roleData={selectedRoleData}
                    opportunities={opportunities.filter(opp => {
                      const roleId = selectedRoleData.id || selectedRoleData.role_id;
                      const roleName = selectedRoleData.title || selectedRoleData.name;
                      const roleDepartment = selectedRoleData.department || '';
                      
                      // Direct role match (primary owner)
                      if (opp.role_id === roleId || opp.linked_role_id === roleId || opp.primary_owner_role_id === roleId) {
                        return true;
                      }
                      
                      // Check if role is in affected_role_ids
                      if (opp.affected_role_ids && Array.isArray(opp.affected_role_ids)) {
                        if (opp.affected_role_ids.includes(roleId)) return true;
                      }
                      
                      // Check by role name in affected_roles
                      if (opp.affected_roles && Array.isArray(opp.affected_roles) && roleName) {
                        if (opp.affected_roles.some(r => r.toLowerCase() === roleName.toLowerCase())) return true;
                      }
                      
                      // FUZZY MATCHING: Match by department (same logic as backend ai_overlay)
                      if (roleDepartment && opp.department) {
                        // Handle both string and array department fields
                        const oppDepts = Array.isArray(opp.department) ? opp.department : [opp.department];
                        if (oppDepts.some(d => d && d.toLowerCase() === roleDepartment.toLowerCase())) {
                          return true;
                        }
                      }
                      
                      // FUZZY MATCHING: Match by title keywords in opportunity title/description
                      if (roleName) {
                        const titleWords = roleName.toLowerCase().split(/[\s&/,\-]+/).filter(w => w.length > 2);
                        const commonWords = new Set(['the', 'and', 'for', 'with', 'from', 'into']);
                        const keywords = titleWords.filter(w => !commonWords.has(w));
                        
                        const oppTitle = (opp.title || '').toLowerCase();
                        const oppDesc = (opp.description || '').toLowerCase();
                        
                        // If any keyword matches the opportunity title or description
                        if (keywords.some(kw => oppTitle.includes(kw) || oppDesc.includes(kw))) {
                          return true;
                        }
                      }
                      
                      return false;
                    })}
                    linkedAgents={linkedAgents.filter(agent => agent.role_id === selectedRoleData.id)}
                    onClose={() => {
                      setShowBottomDrawer(false);
                      setSelectedRoleData(null);
                      setSelectedNodeId(null);
                    }}
                    onCreateAgent={(opp) => {
                      setSelectedRoleForAgent({ ...selectedRoleData, opportunity: opp });
                      setShowCreateAgentModal(true);
                    }}
                    onViewOpportunity={(opp) => {
                      setSelectedOpportunity(opp);
                      setShowOpportunityDrawer(true);
                    }}
                  />
                </div>
              )}
            </div>
          ) : (
            // No data state for visual chart
            <div className="glass rounded-3xl p-8">
              <div className="text-center py-12">
                <Buildings size={64} className="mx-auto mb-6 text-[#6366F1]" />
                <h2 className="text-3xl font-bold text-white mb-4">
                  No Org Chart Data Yet
                </h2>
                <p className="text-xl text-[#A0AEC0] mb-8">
                  UploadSimple your org chart in the assessment or add roles manually to see the visual chart.
                </p>
                <button
                  onClick={() => navigate('/my-assessments')}
                  className="relative overflow-hidden rounded-full group inline-flex"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-[#6366F1] to-[#EC4899]"></div>
                  <span className="relative z-10 flex items-center gap-2 px-8 py-4 text-white font-bold">
                    Go to Assessments
                    <ArrowRight size={20} />
                  </span>
                </button>
              </div>
            </div>
          )
        ) : (
          // Fallback for unknown tab
          <div className="glass rounded-3xl p-12 text-center">
            <p className="text-white">Unknown view</p>
          </div>
        )}
      </div>

      {/* Modals */}
      {showEditor && (
        <OrgChartEditor
          role={editorRole}
          isNew={isNewRole}
          onSave={handleSaveRole}
          onCancel={() => setShowEditor(false)}
          onDelete={handleDeleteRole}
        />
      )}
      
      {showAttachModal && selectedRole && (
        <AttachOpportunityModal
          isOpen={showAttachModal}
          onClose={() => setShowAttachModal(false)}
          role={selectedRole}
          organizationId={activeOrganization?.organization_id}
          onAttach={handleAttachOpportunities}
        />
      )}

      {showUpgradeModal && (
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          currentTier={tierInfo?.tier || 'basic'}
          message="Unlock Org Chart AI Overlay"
          feature="Org Chart Visualization"
        />
      )}

      {/* Create Agent Modal */}
      {showCreateAgentModal && selectedRoleForAgent && (
        <CreateAgentFromRoleModal
          roleData={selectedRoleForAgent}
          organizationId={activeOrganization?.organization_id || 'default_org'}
          onClose={() => {
            setShowCreateAgentModal(false);
            setSelectedRoleForAgent(null);
          }}
          onSuccess={(agent) => {
            setShowCreateAgentModal(false);
            setSelectedRoleForAgent(null);
            fetchOpportunities();
            // Redirect to unified agent builder for activation/configuration
            const agentId = agent?.agent?.agent_id || agent?.agent_id || agent?.id;
            if (agentId) {
              navigate(`/unified-agent-builder/${agentId}`);
            }
          }}
        />
      )}

      {/* Duplicate Roles Modal */}
      {showDuplicatesModal && (
        <DuplicateRolesModal
          isOpen={showDuplicatesModal}
          onClose={() => setShowDuplicatesModal(false)}
          organizationId={activeOrganization?.organization_id}
          onMergeComplete={() => {
            fetchOrgChart();
            fetchUploadedOrgCharts();
            fetchParsedOrgCharts();
          }}
        />
      )}

      {/* Opportunity Detail Drawer - Right slide panel */}
      <OpportunityDetailDrawer
        opportunity={selectedOpportunity}
        isOpen={showOpportunityDrawer}
        onClose={() => {
          setShowOpportunityDrawer(false);
          setSelectedOpportunity(null);
        }}
        onProceedToAgent={(opp) => {
          // Drawer already handles navigation to /partner-pro/ai-workforce/agents/create
          // Just close the drawer and clean up state - no duplicate navigation needed
          setShowOpportunityDrawer(false);
          setSelectedOpportunity(null);
        }}
      />
      </>
  );
};

export default OrgChartPage;
